import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

/**
 * Offre supérieure — config #SuperiorOfferCartConfig.
 *
 * Important : le snapshot « avant » est capturé de façon SYNCHRONE au début
 * du handler (avant tout await). Sinon processGifts / un autre cart:updated
 * écrase previousCartItems à la nouvelle qty et le franchissement disparaît.
 */

const SOURCE = 'superior-offer-bundle';
const originalFetch = window.fetch.bind(window);

const getState = () => {
  if (!window.__superiorOfferCartState) {
    window.__superiorOfferCartState = {
      isSyncing: false,
      previousCartItems: null,
      ready: null,
    };
  }
  return window.__superiorOfferCartState;
};

const getCartSections = () => {
  const sections = ['auto-gifts'];
  const id = document.querySelector('cart-drawer')?.dataset.sectionId;
  if (id) sections.push(id);
  return sections.join(',');
};

const getCart = async () => {
  const response = await originalFetch('/cart.js', { headers: { Accept: 'application/json' } });
  return response.ok ? response.json() : null;
};

const toInt = (raw, fallback = 0) => {
  if (raw == null || raw === '') return fallback;
  if (typeof raw === 'number') return Number.isFinite(raw) ? Math.trunc(raw) : fallback;
  if (typeof raw === 'object' && raw.value != null) return toInt(raw.value, fallback);
  const n = Number.parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) ? n : fallback;
};

const toId = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    const m = raw.match(/\/(\d+)\s*$/);
    if (m) return Number(m[1]);
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const sumQty = (items, productId) => {
  if (!items?.length || productId == null) return 0;
  const pid = Number(productId);
  return items.reduce((sum, item) => {
    if (Number(item.product_id) !== pid) return sum;
    return sum + (parseInt(item.quantity, 10) || 0);
  }, 0);
};

const findLines = (items, productId, variantId) => {
  if (!items?.length) return [];
  const pid = productId != null ? Number(productId) : null;
  const vid = variantId != null ? Number(variantId) : null;
  return items.filter(
    (item) => (pid != null && Number(item.product_id) === pid) || (vid != null && Number(item.variant_id) === vid),
  );
};

const parseConfigText = (text) => {
  if (!text?.trim()) return null;

  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    if (Object.keys(parsed).length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
};

const configFromSections = (sections) => {
  if (!sections) return null;

  const sectionId = document.querySelector('cart-drawer')?.dataset.sectionId;
  const html = sectionId ? sections[sectionId] : null;
  if (typeof html !== 'string' || !html.includes('SuperiorOfferCartConfig')) return null;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  return parseConfigText(doc.getElementById('SuperiorOfferCartConfig')?.textContent);
};

const readConfigMap = (sections) => {
  const state = getState();
  const fromEvent = configFromSections(sections);
  if (fromEvent) {
    state.config = fromEvent;
    return fromEvent;
  }

  const fromDom = parseConfigText(document.getElementById('SuperiorOfferCartConfig')?.textContent);
  if (fromDom) {
    state.config = fromDom;
    return fromDom;
  }

  return state.config || {};
};

const listOffers = (config) => {
  if (!config) return [];
  if (Array.isArray(config.offers)) return config.offers.filter(Boolean);
  if (Array.isArray(config.offer)) return config.offer.filter(Boolean);
  if (config.offer && typeof config.offer === 'object') return [config.offer];
  return [];
};

const normalizeOffer = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.threshold == null && raw.additional_qty_to_add == null && raw.complementary_qty_to_add == null) {
    return null;
  }

  return {
    threshold: toInt(raw.threshold),
    requiredAdditional: toInt(raw.required_additional),
    requiredComplementary: toInt(raw.required_complementary),
    additionalProductId: toId(raw.additional_product_id),
    additionalVariantId: toId(raw.additional_variant_id),
    additionalQtyToAdd: toInt(raw.additional_qty_to_add),
    complementaryProductId: toId(raw.complementary_product_id),
    complementaryVariantId: toId(raw.complementary_variant_id),
    complementaryQtyToAdd: toInt(raw.complementary_qty_to_add),
  };
};

const pickOffer = (rawOffers, mainQty, cartItems) => {
  const offers = rawOffers.map(normalizeOffer).filter((o) => o && o.threshold >= 1 && mainQty >= o.threshold);
  if (offers.length === 0) return null;
  if (offers.length === 1) return offers[0];

  const matching = offers.filter((offer) => {
    const addQty = sumQty(cartItems, offer.additionalProductId);
    const compQty = sumQty(cartItems, offer.complementaryProductId);
    return addQty === offer.requiredAdditional && compQty === offer.requiredComplementary;
  });

  return (matching.length > 0 ? matching : offers)[0];
};

const parseCartJson = async (response) => {
  if (!response.ok) return null;
  const data = await response.json();
  if (data?.errors) {
    console.warn('[superior-offer] cart API errors', data.errors);
    return null;
  }
  return data;
};

const changeLineQty = async (lineKey, quantity) => {
  const response = await originalFetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: lineKey,
      quantity,
      sections: getCartSections(),
      sections_url: window.location.pathname,
    }),
  });
  return parseCartJson(response);
};

const addVariants = async (items) => {
  if (!items.length) return null;
  const response = await originalFetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items,
      sections: getCartSections(),
      sections_url: window.location.pathname,
    }),
  });
  return parseCartJson(response);
};

const lineQty = (items) => items.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);

const existingQty = (items, productId, variantId) =>
  Math.max(sumQty(items, productId), lineQty(findLines(items, null, variantId)));

/** Quantité cible du palier, moins ce qui est déjà au panier. */
const missingQty = (target, already) => Math.max(0, target - already);

const applyDelta = async (cart, { productId, variantId, qtyToAdd, label }) => {
  if (!qtyToAdd || qtyToAdd < 1 || !variantId) {
    if (qtyToAdd > 0) console.warn('[superior-offer] skip', label, { productId, variantId, qtyToAdd });
    return { cart, cartData: null };
  }

  const already = existingQty(cart.items, productId, variantId);
  const missing = missingQty(qtyToAdd, already);

  console.info('[superior-offer] delta', { label, already, target: qtyToAdd, missing });

  if (missing < 1) {
    console.info('[superior-offer] already complete', label, { already, target: qtyToAdd });
    return { cart, cartData: null };
  }

  const lines = findLines(cart.items, productId, variantId);
  let cartData = null;

  if (lines.length > 0) {
    const line = lines[0];
    cartData = await changeLineQty(line.key, (parseInt(line.quantity, 10) || 0) + missing);
  } else {
    cartData = await addVariants([{ id: variantId, quantity: missing }]);
  }

  const nextCart = cartData ? await getCart() : cart;
  return { cart: nextCart || cart, cartData };
};

const applyOffer = async (cart, offer) => {
  let lastCartData = null;
  let nextCart = cart;

  for (const step of [
    {
      label: 'additional',
      productId: offer.additionalProductId,
      variantId: offer.additionalVariantId,
      qtyToAdd: offer.additionalQtyToAdd,
    },
    {
      label: 'complementary',
      productId: offer.complementaryProductId,
      variantId: offer.complementaryVariantId,
      qtyToAdd: offer.complementaryQtyToAdd,
    },
  ]) {
    try {
      const result = await applyDelta(nextCart, step);
      nextCart = result.cart;
      if (result.cartData) lastCartData = result.cartData;
    } catch (error) {
      console.error('[superior-offer] step failed', step.label, error);
    }
  }

  return lastCartData;
};

const didCrossThreshold = (oldQty, newQty, threshold) =>
  oldQty < threshold && newQty >= threshold && newQty > oldQty;

const syncPreviousCartItems = async () => {
  const state = getState();
  const cart = await getCart();
  state.previousCartItems = cart?.items ?? [];
};

/**
 * Handler synchrone en façade : capture le snapshot AVANT tout await.
 */
const onCartUpdated = (event) => {
  const state = getState();
  if (state.isSyncing) return;

  const source = event.detail?.data?.source;
  const sections = event.detail?.data?.sections;
  // Snapshot figé pour ce tour — ne pas relire state.previousCartItems après un await
  const previousItemsSnapshot = state.previousCartItems ?? [];
  const ownsSync = source === 'cart-drawer-item';

  if (ownsSync) state.isSyncing = true;

  void (async () => {
    try {
      if (state.ready) await state.ready;

      // La barre cadeaux du tiroir écrit dans le panier sur le même événement.
      // On la laisse finir, sinon son ajout écrase celui des housses (visible en live, pas en preview).
      if (ownsSync && window.__cartProgressSync) await window.__cartProgressSync;

      const newCart = await getCart();
      if (!newCart?.items) {
        await syncPreviousCartItems();
        return;
      }

      if (!ownsSync) {
        state.previousCartItems = newCart.items;
        return;
      }

      const configMap = readConfigMap(sections);
      const toApply = [];

      for (const [mainProductIdStr, rawConfig] of Object.entries(configMap)) {
        const mainProductId = Number(mainProductIdStr);
        if (!Number.isFinite(mainProductId)) continue;

        const oldQty = sumQty(previousItemsSnapshot, mainProductId);
        const newQty = sumQty(newCart.items, mainProductId);
        const rawOffers = listOffers(rawConfig);

        console.info('[superior-offer] check', {
          mainProductId,
          oldQty,
          newQty,
          source,
          offers: rawOffers.length,
        });

        for (const raw of rawOffers) {
          const offer = normalizeOffer(raw);
          if (!offer?.threshold) continue;
          if (!didCrossThreshold(oldQty, newQty, offer.threshold)) continue;

          const picked = pickOffer(rawOffers, newQty, newCart.items);
          if (!picked) continue;

          toApply.push(picked);
          break;
        }
      }

      // Baseline pour les prochains gestes
      state.previousCartItems = newCart.items;

      if (toApply.length === 0) return;

      state.isSyncing = true;

      let lastCartData = null;
      let cart = newCart;

      for (const offer of toApply) {
        console.info('[superior-offer] apply', {
          housseTarget: offer.additionalQtyToAdd,
          housseAlready: existingQty(cart.items, offer.additionalProductId, offer.additionalVariantId),
        });

        const cartData = await applyOffer(cart, offer);
        if (cartData) {
          lastCartData = cartData;
          cart = (await getCart()) || cart;
        }
      }

      if (lastCartData) {
        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: lastCartData.item_count,
              source: SOURCE,
              sections: lastCartData.sections,
            },
          ),
        );
      }

      await syncPreviousCartItems();
    } catch (error) {
      console.error('[superior-offer] sync failed', error);
      await syncPreviousCartItems();
    } finally {
      if (ownsSync) getState().isSyncing = false;
    }
  })();
};

const state = getState();
if (!state.ready) state.ready = syncPreviousCartItems();

if (window.__superiorOfferCartHandler) {
  document.removeEventListener(ThemeEvents.cartUpdated, window.__superiorOfferCartHandler);
}
window.__superiorOfferCartHandler = onCartUpdated;
document.addEventListener(ThemeEvents.cartUpdated, onCartUpdated);

console.info('[superior-offer] ready');
